import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import apiClient from '../services/api/config';
import { useAuth } from './AuthContext';
import { pushNotificationService } from '../services/pushNotificationService';
import type { PermissionState } from '../services/pushNotificationService';

export interface NotificationCounts {
  announcements: number;
  policies: number;
  learning: number;
  games: number;
  rewards: number;
  pulse: number;
  tasks: number;
  documents: number;
  calendar: number;
  recognition: number;
  total: number;
  // Badge-only counts for the navigation rail (not part of `total`).
  store?: number;
  chat?: number;
}

interface NotificationContextType {
  counts: NotificationCounts;
  refreshCounts: () => Promise<void>;
  markAsRead: (module: keyof NotificationCounts, count?: number) => void;
  isLoading: boolean;
  /** Push notification helpers */
  pushPermission: PermissionState;
  isPushSubscribed: boolean;
  subscribeToPush: () => Promise<boolean>;
  unsubscribeFromPush: () => Promise<boolean>;
  /** Whether the permission prompt banner should be shown */
  showPermissionPrompt: boolean;
  dismissPermissionPrompt: () => void;
}

const defaultCounts: NotificationCounts = {
  announcements: 0,
  policies: 0,
  learning: 0,
  games: 0,
  rewards: 0,
  pulse: 0,
  tasks: 0,
  documents: 0,
  calendar: 0,
  recognition: 0,
  total: 0,
};

const PROMPT_DISMISSED_KEY = 'moreme_push_prompt_dismissed';

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const { isAuthenticated } = useAuth();
  const [counts, setCounts] = useState<NotificationCounts>(defaultCounts);
  const [isLoading, setIsLoading] = useState(false);
  const [pushPermission, setPushPermission] = useState<PermissionState>(
    pushNotificationService.getPermission()
  );
  const [isPushSubscribed, setIsPushSubscribed] = useState(false);
  const [showPermissionPrompt, setShowPermissionPrompt] = useState(false);
  const prevTotalRef = useRef(0);

  // ─── Push initialisation (runs once on mount when logged in) ──
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    if (!pushNotificationService.isSupported()) return;

    const perm = pushNotificationService.getPermission();
    setPushPermission(perm);

    if (perm === 'granted') {
      // Already granted – silently subscribe (idempotent)
      pushNotificationService.subscribe().then(() => {
        pushNotificationService.isSubscribed().then(setIsPushSubscribed);
      });
    } else if (perm === 'default') {
      // Show prompt banner (unless user previously dismissed it) — deferred so
      // it never stacks with the first-login moment (cookie consent, dashboard
      // entrance). One quiet ask, 45s in, and only if the tab is still open.
      const dismissed = localStorage.getItem(PROMPT_DISMISSED_KEY);
      if (!dismissed) {
        const t = setTimeout(() => setShowPermissionPrompt(true), 45000);
        return () => clearTimeout(t);
      }
    }
  }, []);

  const subscribeToPush = useCallback(async () => {
    const ok = await pushNotificationService.subscribe();
    setPushPermission(pushNotificationService.getPermission());
    setIsPushSubscribed(ok);
    setShowPermissionPrompt(false);
    return ok;
  }, []);

  const unsubscribeFromPush = useCallback(async () => {
    const ok = await pushNotificationService.unsubscribe();
    setIsPushSubscribed(!ok);
    return ok;
  }, []);

  const dismissPermissionPrompt = useCallback(() => {
    setShowPermissionPrompt(false);
    localStorage.setItem(PROMPT_DISMISSED_KEY, '1');
  }, []);

  // ─── Notification counts ──────────────────────────────────────

  const refreshCounts = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch notification counts from API
      // Background poll: a failure must never raise a toast.
      const response = await apiClient.get('/notifications/counts', { skipErrorToast: true });
      if (response.data?.success && response.data?.data) {
        const data = response.data.data;
        const newCounts: NotificationCounts = {
          announcements: data.announcements || 0,
          policies: data.policies || 0,
          learning: data.learning || 0,
          games: data.games || 0,
          rewards: data.rewards || 0,
          pulse: data.pulse || 0,
          tasks: data.tasks || 0,
          documents: data.documents || 0,
          calendar: data.calendar || 0,
          recognition: data.recognition || 0,
          total: 0,
        };
        // Calculate total (the module counts only; store/chat are rail badges)
        newCounts.total = Object.values(newCounts).reduce((sum, val) => sum + val, 0);
        newCounts.store = data.store || 0;
        newCounts.chat = data.chat || 0;
        setCounts(newCounts);
      }
    } catch (error) {
      // If API fails, try to get counts from individual endpoints
      try {
        const [announcementsRes, policiesRes, learningRes] = await Promise.all([
          apiClient.get('/announcements?unread=true', { skipErrorToast: true }).catch(() => ({ data: { data: [] } })),
          apiClient.get('/policies?unread=true', { skipErrorToast: true }).catch(() => ({ data: { data: [] } })),
          apiClient.get('/learning/new', { skipErrorToast: true }).catch(() => ({ data: { data: [] } })),
        ]);

        const newCounts: NotificationCounts = {
          announcements: Array.isArray(announcementsRes.data?.data) ? announcementsRes.data.data.filter((a: any) => !a.isRead).length : 0,
          policies: Array.isArray(policiesRes.data?.data) ? policiesRes.data.data.filter((p: any) => !p.isRead).length : 0,
          learning: Array.isArray(learningRes.data?.data) ? learningRes.data.data.length : 0,
          games: 0,
          rewards: 0,
          pulse: 0,
          tasks: 0,
          documents: 0,
          calendar: 0,
          recognition: 0,
          total: 0,
        };
        newCounts.total = Object.values(newCounts).reduce((sum, val) => sum + val, 0);
        setCounts(newCounts);
      } catch {
        // Silently fail - keep default counts
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ─── Badge + push notification on count changes ───────────────

  useEffect(() => {
    // Update the app badge whenever total count changes
    pushNotificationService.setBadge(counts.total);

    // If count went UP compared to previous, fire a local push notification
    if (counts.total > prevTotalRef.current && prevTotalRef.current > 0) {
      const diff = counts.total - prevTotalRef.current;
      pushNotificationService.showNotification({
        title: 'MoreMe AI',
        body: `You have ${diff} new item${diff > 1 ? 's' : ''} waiting for you!`,
        tag: 'moreme-count-update',
        url: '/dashboard',
        type: 'general',
        badgeCount: counts.total,
      });
    }
    prevTotalRef.current = counts.total;
  }, [counts.total]);

  const markAsRead = useCallback((module: keyof NotificationCounts, count = 1) => {
    setCounts(prev => {
      const newCount = Math.max(0, (prev[module] || 0) - count);
      const newTotal = prev.total - ((prev[module] || 0) - newCount);
      return {
        ...prev,
        [module]: newCount,
        total: Math.max(0, newTotal),
      };
    });
  }, []);

  // The one place the notification count is polled (the navigation rail reads
  // it from here). Polling pauses while the tab is hidden and catches up the
  // moment it is visible again.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !isAuthenticated) return;
    const poll = () => {
      const t = localStorage.getItem('token');
      if (!t || document.hidden) return;
      if (window.location.pathname === '/questionnaire') return;
      refreshCounts();
    };
    poll();
    const interval = setInterval(poll, 60000);
    document.addEventListener('visibilitychange', poll);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [refreshCounts, isAuthenticated]);

  const value: NotificationContextType = {
    counts,
    refreshCounts,
    markAsRead,
    isLoading,
    pushPermission,
    isPushSubscribed,
    subscribeToPush,
    unsubscribeFromPush,
    showPermissionPrompt,
    dismissPermissionPrompt,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
